import { cx, useTranslations } from "@jugl-web/utils";
import { Button } from "@jugl-web/ui-components";
import { useState } from "react";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useEntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { useUserAuth } from "@web-src/modules/auth/providers/UserAuthProvider/useUserAuth";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { SignUpFlowLayout } from "../SignUpFlowLayout";
import { SignUpFlowContentLayout } from "../../components/SignUpFlowContentLayout/SignUpFlowContentLayout";
import { AuthDiscardConfirmationAlert } from "../../components/AuthDiscardConfirmationAlert/AuthDiscardConfirmationAlert";

const OptionButton = ({
  label,
  isActive = false,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <div
    className={cx(
      "relative flex cursor-pointer items-center gap-4 rounded-[10px] border-[1px] border-solid border-grey-400 p-[36px] transition-all duration-500",
      { "border-transparent": isActive }
    )}
    onClick={onClick}
  >
    {/* Gradient background with fade effect */}
    <div
      className={cx(
        "absolute inset-0 rounded-[10px] bg-gradient-to-r from-[#3030DA] to-[#7F2A83] p-[1px] transition-opacity duration-500",
        isActive ? "opacity-100" : "opacity-0" // Fade in/out with opacity
      )}
    >
      <div className="absolute inset-[2px] rounded-[9px] bg-white" />
    </div>

    <div
      className={cx(
        "z-10 h-4 w-4 rounded-full border border-solid border-[#D8DAE5] transition-all duration-500",
        {
          "border-[4px] border-[#3030DA]": isActive,
        }
      )}
    />
    <div className="z-10 text-dark">{label}</div>
  </div>
);

export const SignUpPurpose = () => {
  const { t } = useTranslations();
  const { logout } = useUserAuth();
  const { id: meId } = useMe();

  const [isDiscardAlertOpen, setIsDiscardAlertOpen] = useState(false);
  const { navigateToPage } = useNavigation();
  const [option, setOption] = useState<
    "invitations" | "create-workspace" | null
  >(null);
  const { usersApi } = useRestApiProvider();
  const {
    entityInvitesState: { entityInvites },
  } = useEntitiesListProvider();

  const { data: userProfile } = usersApi.useGetUserProfileQuery(
    meId ? { params: { user_id: meId } } : {}
  );

  const name = userProfile?.general.first_name || "";

  return (
    <>
      <AuthDiscardConfirmationAlert
        isOpen={Boolean(isDiscardAlertOpen)}
        variant="logout"
        onConfirm={() => {
          logout(false);
        }}
        onClose={() => setIsDiscardAlertOpen(false)}
      />

      <SignUpFlowLayout
        type="signUpPurpose"
        progressBar={40}
        footerClassName="pb-6 px-[64px] lg:px-0 lg:pr-[64px] lg:pl-[32px]"
        onBackClick={() => setIsDiscardAlertOpen(true)}
        showMoreMenu
      >
        <SignUpFlowContentLayout
          titleSectionClassName="w-[440px]"
          title={
            name.length > 20
              ? t({
                  id: "sign-up-purpose-page.title-short",
                  defaultMessage: "Hey! How should we set things up?",
                })
              : t(
                  {
                    id: "sign-up-purpose-page.title-2",
                    defaultMessage: "Hey {name}! How should we set things up?",
                  },
                  {
                    name,
                  }
                )
          }
          subtitle={t({
            id: "sign-up-purpose-page.subtitle",
            defaultMessage:
              "Tell us what you’re here for and we’ll take care of the rest.  💫",
          })}
          description={
            <div className="flex w-[514px] flex-col gap-4 pb-[64px]">
              <OptionButton
                label={t({
                  id: "sign-up-purpose-page.joining-team-workspace",
                  defaultMessage: "Joining my team’s workspace",
                })}
                onClick={() => setOption("invitations")}
                isActive={option === "invitations"}
              />
              <OptionButton
                label={t({
                  id: "sign-up-purpose-page.create-workspace",
                  defaultMessage: "Creating a new workspace",
                })}
                onClick={() => setOption("create-workspace")}
                isActive={option === "create-workspace"}
              />
            </div>
          }
          footer={
            <Button
              className="mx-auto w-[300px] font-medium"
              type="submit"
              size="small"
              isDisabled={!option}
              onClick={() => {
                if (option === "invitations") {
                  navigateToPage(
                    entityInvites?.length
                      ? "authInvitations"
                      : "authNoInvitations"
                  );
                }
                if (option === "create-workspace") {
                  navigateToPage("authNewWorkspace", {
                    flow: "signup",
                  });
                }
              }}
            >
              {t({
                id: "common.continue",
                defaultMessage: "Continue",
              })}
            </Button>
          }
        />
      </SignUpFlowLayout>
    </>
  );
};
